import { ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppSidebar } from "./AppSidebar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/useAuth";
import { getInitials } from "@/lib/utils";
import { ThemedLogo } from "@/components/shared/ThemedLogo";
import { useOrphanedConversations } from "@/hooks/useOrphanedConversations";

interface MainLayoutProps {
  children: ReactNode;
}

export function MainLayout({ children }: MainLayoutProps) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, profileName } = useAuth();
  useOrphanedConversations();

  if (isMobile) {
    return (
      <div className="min-h-screen bg-background overflow-x-hidden">
        {/* Mobile Header - Three Column Grid */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-background h-14 grid grid-cols-3 items-center px-4">
          {/* Left - Hamburger Menu */}
          <div className="flex justify-start">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-11 w-11">
                  <Menu className="h-9 w-9" strokeWidth={2.5} />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-64">
                <AppSidebar />
              </SheetContent>
            </Sheet>
          </div>

          {/* Center - Logo and Branding */}
          <button
            onClick={() => navigate("/feed")}
            className="flex items-center justify-center"
          >
            <ThemedLogo className="h-[52px] w-auto object-contain" />
          </button>

          {/* Right - User Avatar */}
          <div className="flex justify-end">
            <button onClick={() => navigate("/profile")}>
              <Avatar className="h-9 w-9 border-2 border-primary/20">
                <AvatarFallback className="bg-muted text-muted-foreground text-sm">
                  {getInitials(profileName || user?.user_metadata?.full_name)}
                </AvatarFallback>
              </Avatar>
            </button>
          </div>
        </header>

        {/* Content with top padding for mobile header */}
        <main className="pt-14 min-h-screen overflow-x-hidden">{children}</main>
      </div>
    );
  }

  // Desktop layout
  return (
    <div className="min-h-screen bg-background flex">
      <AppSidebar />
      <main className="flex-1 min-h-screen overflow-auto">{children}</main>
    </div>
  );
}
